import { useState, useEffect, useCallback, useMemo } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { Dropdownlist } from '@/components/Dropdownlist';

export interface DispositionOption {
  dispositionCode: number;
  dispositionValue: string;
  [key: string]: any;
}

export interface CloseCallModalProps {
  open: boolean;
  dispositionOptions?: DispositionOption[];
  dispositionGroupBy?: string | null;
  currentDisposition?: number | null;
  currentDispositionNotes?: string;
  theme?: string;
  onClose: (data: { disposition: number | null; dispositionNotes: string }) => void;
  onCancel: () => void;
}

export function CloseCallModal({
  open,
  dispositionOptions = [],
  dispositionGroupBy = null,
  currentDisposition = null,
  currentDispositionNotes = '',
  theme,
  onClose,
  onCancel,
}: CloseCallModalProps) {
  const [localDisposition, setLocalDisposition] = useState<number | null>(null);
  const [localDispositionNotes, setLocalDispositionNotes] = useState('');
  const [touched, setTouched] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setLocalDisposition(currentDisposition);
      setLocalDispositionNotes(currentDispositionNotes || '');
      setTouched(false);
    }
  }, [open, currentDisposition, currentDispositionNotes]);

  // Sync currentDisposition prop changes while open
  useEffect(() => {
    if (open) {
      setLocalDisposition(currentDisposition);
    }
  }, [currentDisposition, open]);

  // Sync currentDispositionNotes prop changes while open
  useEffect(() => {
    if (open) {
      setLocalDispositionNotes(currentDispositionNotes || '');
    }
  }, [currentDispositionNotes, open]);

  const isValid = useMemo(() => {
    return (
      localDisposition !== null &&
      localDisposition !== -1 &&
      localDisposition !== undefined
    );
  }, [localDisposition]);

  const showError = useMemo(() => {
    return touched && !isValid;
  }, [touched, isValid]);

  const handleClose = useCallback(() => {
    setTouched(true);
    if (
      localDisposition === null ||
      localDisposition === -1 ||
      localDisposition === undefined
    ) {
      return;
    }
    onClose({
      disposition: localDisposition,
      dispositionNotes: localDispositionNotes,
    });
  }, [localDisposition, localDispositionNotes, onClose]);

  const onDispositionChange = useCallback((value: string | number | null) => {
    setTouched(true);
    if (typeof value === 'number') {
      setLocalDisposition(value);
    } else {
      setLocalDisposition(null);
    }
  }, []);

  return (
    <BaseFormModal
      open={open}
      title="Close Call"
      theme={theme}
      dialogClass="close-call-dialog"
      saveText="Close Call"
      saveButtonClass={`kstc-btn--danger${!isValid ? ' kstc-btn--disabled' : ''}`}
      onSave={handleClose}
      onCancel={onCancel}
    >
      <p className="close-call-instruction">
        Please confirm or update the disposition before closing this call.
      </p>

      <div className="close-call-field close-call-disposition">
        <label className="close-call-label">
          Disposition <span className="required">*</span>
        </label>
        <Dropdownlist
          items={dispositionOptions}
          value={localDisposition}
          labelKey="dispositionValue"
          valueKey="dispositionCode"
          groupBy={dispositionGroupBy}
          placeholder="Select Disposition"
          searchable={true}
          sortItems={false}
          onChange={onDispositionChange}
        />
        {showError && (
          <span className="close-call-error">
            <i className="fas fa-exclamation-circle" />
            Disposition is required to close the call
          </span>
        )}
      </div>

      <div className="close-call-field">
        <label className="close-call-label" htmlFor="closeCallDispositionNotes">
          Disposition Notes
        </label>
        <textarea
          id="closeCallDispositionNotes"
          className="close-call-textarea"
          rows={4}
          placeholder="Enter any additional notes about the disposition..."
          value={localDispositionNotes}
          onChange={(e) => setLocalDispositionNotes(e.target.value)}
        />
      </div>
    </BaseFormModal>
  );
}

(window as any).CloseCallModalReact = CloseCallModal;
